import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export const AdminPaymentStatusCell = ({ type, row, onClick }) => {
  const handleClick = (event) => {
    if (!onClick) return;
    event.stopPropagation();
    onClick(type, row);
  };
  const clickableSx = onClick ? {
    cursor: 'pointer',
    borderRadius: '6px',
    px: 0.45,
    py: 0.25,
    '&:hover': { bgcolor: 'rgba(6,85,165,0.08)' },
  } : {};

  if (type === 'entrada' || type === 'comprobante' || type === 'pago') {
    const isEntrada = type === 'entrada';
    const isComprobante = type === 'comprobante';
    const count = Number(isEntrada
      ? row.ENTRADAS_COUNT
      : (isComprobante ? row.COMPROBANTE_COUNT : (row.PAGO_OPERATIVO_COUNT ?? row.PAGO_ERP_COUNT)) || 0);
    const comprobanteCount = Number(row.COMPROBANTE_COUNT || 0);
    const erpAbonos = Number(String(row.PAGO_ERP_ABONOS || '').replace(/[^0-9.-]+/g, '')) || 0;
    const erpSaldo = Number(String(row.PAGO_ERP_SALDO || '').replace(/[^0-9.-]+/g, '')) || 0;
    const pagoOperativoMonto = row.PAGO_OPERATIVO_ABONOS || row.PAGO_OPERATIVO_TOTAL || row.PAGO_ERP_ABONOS || row.PAGO_ERP_TOTAL || '$0.00';
    const pagoOperativoFecha = row.PAGO_OPERATIVO_FECHA || row.PAGO_ERP_FECHA || '';
    const pagoOperativoCompleto = (row.PAGO_OPERATIVO_COMPLETO === true || String(row.PAGO_OPERATIVO_COMPLETO).toUpperCase() === 'TRUE') && erpSaldo <= 0;
    const pagoOperativoAbonos = Number(String(pagoOperativoMonto || '').replace(/[^0-9.-]+/g, '')) || 0;
    const complete = isEntrada
      ? row.ENTRADA_COMPLETA === true
      : (isComprobante ? (row.TIENE_COMPROBANTE === true || comprobanteCount > 0) : (pagoOperativoCompleto || (row.PAGO_ERP_COMPLETO === true && erpSaldo <= 0)));
    const hasAny = isEntrada
      ? count > 0
      : (isComprobante ? comprobanteCount > 0 : (count > 0 || erpAbonos > 0 || pagoOperativoAbonos > 0 || comprobanteCount > 0));
    const primary = isEntrada
      ? (complete ? 'Completa' : (count > 0 ? 'Parcial' : 'Sin entrada'))
      : (isComprobante
        ? (complete ? 'Cargado' : 'Sin Comp')
        : (complete ? 'Pagado' : (hasAny ? 'Pago parcial' : 'Sin pago')));
    const secondaryBase = isEntrada
      ? (count > 0 ? `${row.ENTRADAS_TOTAL || '$0.00'}${row.ENTRADA_FECHA ? ` · ${row.ENTRADA_FECHA}` : ''}` : 'Sin registro')
      : (isComprobante
        ? (complete ? `${comprobanteCount} ${comprobanteCount === 1 ? 'archivo' : 'archivos'}` : 'Pendiente')
        : (complete || hasAny ? `${row.PAGO_ERP_ABONOS || row.PAGO_ERP_TOTAL || '$0.00'}${row.PAGO_ERP_FECHA ? ` · ${row.PAGO_ERP_FECHA}` : ''}` : 'Pendiente'));
    const secondary = isEntrada || isComprobante
      ? secondaryBase
      : (complete || hasAny ? `${pagoOperativoMonto}${pagoOperativoFecha ? ` - ${pagoOperativoFecha}` : ''}` : 'Pendiente');
    const tooltip = isEntrada
      ? [
        row.ENTRADA_FOLIOS ? `Entradas: ${row.ENTRADA_FOLIOS}` : '',
        row.ENTRADAS_TOTAL ? `Total entrada: ${row.ENTRADAS_TOTAL}` : '',
        row.ENTRADA_FECHA ? `Ultima entrada: ${row.ENTRADA_FECHA}` : '',
      ].filter(Boolean).join('\n')
      : (isComprobante ? [
        complete ? `Comprobantes: ${comprobanteCount}` : 'No hay comprobante cargado',
      ].filter(Boolean).join('\n') : [
        row.PAGO_ERP_RECIBOS ? `Recibos: ${row.PAGO_ERP_RECIBOS}` : '',
        row.PAGO_ERP_ABONOS ? `Abonado ERP: ${row.PAGO_ERP_ABONOS}` : '',
        row.PAGO_ERP_SALDO ? `Saldo ERP: ${row.PAGO_ERP_SALDO}` : (erpSaldo > 0 ? `Saldo ERP: ${erpSaldo}` : ''),
        row.PAGO_ERP_FECHA ? `Ultimo pago ERP: ${row.PAGO_ERP_FECHA}` : '',
        row.PAGO_OPERATIVO_ORIGEN ? `Detectado por: ${row.PAGO_OPERATIVO_ORIGEN}` : '',
        row.PAGO_OPERATIVO_FECHA ? `Ultimo movimiento: ${row.PAGO_OPERATIVO_FECHA}` : '',
      ].filter(Boolean).join('\n'));
    const iconColor = complete ? '#15803d' : (hasAny ? '#f59e0b' : '#94a3b8');
    const textColor = complete ? '#14532d' : (hasAny ? '#92400e' : '#64748b');

    return (
      <Box onClick={handleClick} sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0, ...clickableSx }}>
        {complete ? (
          <CheckCircleIcon sx={{ color: iconColor, fontSize: 17, flexShrink: 0 }} />
        ) : (
          <ErrorOutlineIcon sx={{ color: iconColor, fontSize: 17, flexShrink: 0 }} />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.10vw, 0.74rem)', color: textColor, fontWeight: 900, lineHeight: 1.05, whiteSpace: 'nowrap' }}>
            {primary}
          </Typography>
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>} arrow disableInteractive>
            <Typography sx={{ mt: 0.2, fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', color: '#64748b', fontWeight: 800, lineHeight: 1.05, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 98 }}>
              {secondary}
            </Typography>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  const isReporte = type === 'reporte';
  const isSolicitud = type === 'solicitud';
  const authFlow = Array.isArray(row.cadenaAut)
    ? row.cadenaAut.filter(item => String(item?.ROL || '').startsWith('Autorizador '))
    : [];
  const nextPendingAuth = authFlow.find(item => item?.ESTADO !== 'CHECK_GREEN');
  const authComplete = authFlow.length > 0 && authFlow.every(item => item?.ESTADO === 'CHECK_GREEN');
  const count = type === 'solicitud'
    ? Number(row.SOLICITUDES_COUNT || 0)
    : (isReporte ? Number(row.REPORTE_SOLICITADO_COUNT || 0) : Number(row.PAGADAS_COUNT || 0));
  const isOk = isSolicitud ? count > 0 && authComplete : count > 0;
  const isPending = isSolicitud && count > 0 && !authComplete;
  const date = type === 'solicitud'
    ? row.ULTIMA_SOLICITUD
    : (isReporte ? row.REPORTE_SOLICITADO_FECHA : row.ULTIMO_PAGO);
  const user = isReporte ? row.REPORTE_SOLICITADO_USUARIO : (type === 'pago' ? row.USUARIO_PAGO : '');
  const eventos = Array.isArray(row.REPORTE_SOLICITADO_EVENTOS) ? row.REPORTE_SOLICITADO_EVENTOS : [];
  const authTooltip = isSolicitud && count > 0
    ? [
      authComplete ? 'Autorización completa' : `Detenido con: ${nextPendingAuth?.USUARIO || 'autorizador pendiente'}`,
      ...authFlow.map(item => `${item.ROL}: ${item.USUARIO || 'N/A'} - ${item.ESTADO === 'CHECK_GREEN' ? 'Autorizó' : 'Pendiente'}${item.FECHA_AUTORIZACION ? ` (${item.FECHA_AUTORIZACION})` : ''}`)
    ].join('\n')
    : '';
  const tooltip = isSolicitud
    ? authTooltip
    : (isReporte && eventos.length
      ? eventos.map(evento => `${evento.fecha || ''} - ${evento.usuario || 'Sistema'}`.trim()).join('\n')
      : [date, user].filter(Boolean).join(' - '));
  const primary = type === 'solicitud'
    ? (count > 0 ? (authComplete ? 'Completa' : 'En proceso') : 'Sin solicitud')
    : (isReporte ? (isOk ? 'Solicitada' : 'Sin pedir') : (isOk ? 'Pagada' : 'Sin pago'));
  const secondary = type === 'solicitud'
    ? (count > 0
      ? (authComplete
        ? (count > 1 ? 'Historial relacionado' : '1 solicitud')
        : `Sigue: ${nextPendingAuth?.USUARIO || 'pendiente'}`)
      : 'Sin registro')
    : (isReporte
      ? (isOk ? `${count} ${count === 1 ? 'vez' : 'veces'}${user ? ` · ${user}` : ''}` : 'No seleccionado')
      : (isOk ? `${user || 'Registrado'}${date ? ` · ${date}` : ''}` : 'Pendiente'));
  const iconColor = isOk ? '#15803d' : (isPending ? '#f59e0b' : '#94a3b8');
  const textColor = isOk ? '#14532d' : (isPending ? '#92400e' : '#64748b');
  const showMultipleSolicitud = type === 'solicitud' && count > 1;

  return (
    <Box onClick={handleClick} sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0, ...clickableSx }}>
      {isOk ? (
        <CheckCircleIcon sx={{ color: iconColor, fontSize: 17, flexShrink: 0 }} />
      ) : (
        <ErrorOutlineIcon sx={{ color: iconColor, fontSize: 17, flexShrink: 0 }} />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.10vw, 0.74rem)', color: textColor, fontWeight: 900, lineHeight: 1.05, whiteSpace: 'nowrap' }}>
          {primary}
        </Typography>
        {showMultipleSolicitud && (
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              mt: 0.25,
              px: 0.55,
              py: 0.08,
              borderRadius: '6px',
              bgcolor: '#005eb8',
              color: '#fff',
              fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)',
              fontWeight: 900,
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
            }}
          >
            {count} solicitudes
          </Box>
        )}
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>} arrow disableInteractive>
          <Typography sx={{ mt: showMultipleSolicitud ? 0.15 : 0.2, fontSize: 'clamp(0.50rem, 0.48rem + 0.08vw, 0.62rem)', color: '#64748b', fontWeight: 800, lineHeight: 1.05, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: isReporte ? 220 : 90 }}>
            {secondary}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default AdminPaymentStatusCell;